import React, { useState } from "react";
import styled from "styled-components";
import emailjs from "emailjs-com";
import { motion } from "framer-motion";
import portfolio from "../content/portfolio.json";
import PageHelmet from "../components/PageHelmet";

const { meta, contact } = portfolio;

// Initialize EmailJS globally with the public key
emailjs.init(process.env.REACT_APP_EMAILJS_PUBLIC_KEY);

const ContactContainer = styled.div`
    display: grid;
    grid-template-columns: 1fr 2fr; /* Image takes 1/3, form takes 2/3 */
    align-items: center;
    justify-content: center;
    height: 100vh;
    width: 100%; /* Full width */
    max-width: 1200px; /* Prevents excessive stretching on large screens */
    margin: 0 auto; /* Centers the container */
    padding: 20px;

    @media (max-width: 768px) {
        grid-template-columns: 1fr; /* Stack vertically on smaller screens */
        text-align: center;
    }
`;

const LeftColumn = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
`;

const ProfileImage = styled.img`
    width: 500px;
    height: 500px;
    border-radius: 50%;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2);

    @media (max-width: 768px) {
        width: 120px;
        height: 120px;
    }
`;

const RightColumn = styled(motion.div)` /* Animated for smooth transitions */
    text-align: center;
    padding: 20px;

    @media (max-width: 768px) {
        margin-top: 20px; /* Add spacing between sections on mobile */
    }
`;

const Title = styled.h1`
    font-size: 2.5rem;
    margin-bottom: 20px;
    color: #0078d7;
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: 15px;
    width: 100%;
    max-width: 600px;
    margin: 0 auto;
`;

const Input = styled.input`
    padding: 10px;
    font-size: 1rem;
    border: 1px solid #ccc;
    border-radius: 5px;
    width: 100%;
    box-sizing: border-box;

    &:focus {
        border-color: #0078d7;
        outline: none;
        box-shadow: 0 0 5px rgba(0, 120, 215, 0.5);
    }
`;

const TextArea = styled.textarea`
    padding: 10px;
    font-size: 1rem;
    border: 1px solid #ccc;
    border-radius: 5px;
    width: 100%;
    box-sizing: border-box;
    resize: vertical;

    &:focus {
        border-color: #0078d7;
        outline: none;
        box-shadow: 0 0 5px rgba(0, 120, 215, 0.5);
    }
`;

const SubmitButton = styled.button`
    padding: 10px 20px;
    font-size: 1rem;
    background-color: #0078d7;
    color: #fff;
    border: none;
    border-radius: 5px;
    cursor: pointer;
    transition: background-color 0.3s;

    &:hover {
        background-color: #005bb5;
    }
`;

const Contact = () => {
    const [formData, setFormData] = useState({ name: "", email: "", message: "" });
    const [isSubmitted, setIsSubmitted] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = (e) => {
        e.preventDefault();

            // Use EmailJS to send the email
            emailjs
            .send(
                process.env.REACT_APP_EMAILJS_SERVICE_ID,
                process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
                {
                    name: formData.name,
                    email: formData.email,
                    message: formData.message,
                },
            )
            .then(
                (result) => {
                    console.log("Email sent successfully:", result.text);
                    setIsSubmitted(true); // Show success message
                },
                (error) => {
                    console.error("Error sending email:", error.text);
                    alert("Failed to send the message. Please try again.");
                }
            );
    };

    return (
        <>
        <PageHelmet title={meta.pageTitles.contact} />
        <ContactContainer>
            {/* Left Column: Profile Image */}
            <LeftColumn>
                <ProfileImage src="/images/myphoto.jpg" alt={meta.name} />
            </LeftColumn>

            {/* Right Column: Contact Form */}
            <RightColumn
                initial={{ opacity: 0, x: 50 }} /* Starting animation state */
                animate={{ opacity: 1, x: 0 }} /* Final animation state */
                exit={{ opacity: 0, x: -50 }} /* Exit animation */
                transition={{ duration: 0.5 }} /* Duration of animation */
            >
                <Title>{contact.title}</Title>
                {!isSubmitted ? (
                    <Form onSubmit={handleSubmit}>
                        <Input
                            type="text"
                            name="name"
                            placeholder={contact.placeholders.name}
                            value={formData.name}
                            onChange={handleChange}
                            required
                        />
                        <Input
                            type="email"
                            name="email"
                            placeholder={contact.placeholders.email}
                            value={formData.email}
                            onChange={handleChange}
                            required
                        />
                        <TextArea
                            name="message"
                            placeholder={contact.placeholders.message}
                            rows="5"
                            value={formData.message}
                            onChange={handleChange}
                            required
                        />
                        <SubmitButton type="submit">{contact.submitButton}</SubmitButton>
                    </Form>
                ) : (
                    <p>{contact.successMessage}</p>
                )}
            </RightColumn>
        </ContactContainer>
        </>
    );
};

export default Contact;